import { Injectable } from '@angular/core';
import { IMarkerPoint, IPoint } from '../IPoint';

const EARTH_RADIUS_IN_METERS: number = 6.3781e6;

@Injectable()
export class MapComputationService {
    public computeDistance(point1: IPoint, point2: IPoint): number {
        // source https://stackoverflow.com/questions/27928/calculate-distance-between-two-latitude-longitude-points-haversine-formula

        const dLat: number = deg2rad(point2.latitude - point1.latitude);
        const dLon: number = deg2rad(point2.longitude - point1.longitude);

        const a: number =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(deg2rad(point1.latitude)) * Math.cos(deg2rad(point2.latitude)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c: number = Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 2;

        return EARTH_RADIUS_IN_METERS * c;
    }

    public computeOffset(origin: IPoint, bearingInDegreesClockwiseFromNorth: number, distanceInMeters: number): IPoint {
        // Source https://stackoverflow.com/questions/7222382/get-lat-long-given-current-point-distance-and-bearing

        const lat1: number = deg2rad(origin.latitude);
        const long1: number = deg2rad(origin.longitude);
        const bearing: number = deg2rad(bearingInDegreesClockwiseFromNorth);

        const lat2: number = Math.asin(Math.sin(lat1) * Math.cos(distanceInMeters / EARTH_RADIUS_IN_METERS) +
            Math.cos(lat1) * Math.sin(distanceInMeters / EARTH_RADIUS_IN_METERS) * Math.cos(bearing));

        const long2: number = long1 + Math.atan2(Math.sin(bearing) * Math.sin(distanceInMeters / EARTH_RADIUS_IN_METERS) * Math.cos(lat1),
            Math.cos(distanceInMeters / EARTH_RADIUS_IN_METERS) - Math.sin(lat1) * Math.sin(lat2));

        return {
            latitude: rad2deg(lat2),
            longitude: rad2deg(long2),
        };
    }

    public haveChangedPoints(oldPoints: IMarkerPoint[], newPoints: IMarkerPoint[]): boolean {
        if (!oldPoints && newPoints) {
            return true;
        }

        if (oldPoints.length !== newPoints.length) {
            return true;
        }

        const oldLabels: string = oldPoints.map((item: IMarkerPoint) => item.label).sort().toString();
        const newLabels: string = newPoints.map((item: IMarkerPoint) => item.label).sort().toString();
        return oldLabels !== newLabels;
    }
}

function deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
}

function rad2deg(radians: number): number {
    return radians / (Math.PI / 180);
}